import { useCallback, useEffect, useState } from 'react';
import { TextAa, Trash, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ACCEPT = '.ttf,.otf,.woff,.woff2,.zip';

export default function DesignDnaV2FontLibrary({ api, projectId, compact = false, onChange }) {
  const [fonts, setFonts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/design/v2/fonts`);
      const next = data.fonts || [];
      setFonts(next);
      onChange?.(next);
    } catch (e) {
      toast.error(`Font Library: ${e.response?.data?.detail || e.message}`);
    }
  }, [api, projectId, onChange]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length || !projectId) return;
    setUploading(true);
    const form = new FormData();
    selectedFiles.forEach(file => form.append('files', file));
    try {
      const { data } = await api.post(`/projects/${projectId}/design/v2/fonts`, form);
      await load();
      const count = Array.isArray(data?.fonts) ? data.fonts.length : selectedFiles.length;
      toast.success(`${count} font aggiunti`);
    } catch (e) {
      toast.error(`Font Library: ${e.response?.data?.detail || e.message}`);
    } finally { setUploading(false); }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    void upload(selectedFiles);
  };

  const remove = async (assetId) => {
    setRemoving(assetId);
    try { await api.delete(`/projects/${projectId}/design/v2/fonts/${assetId}`); await load(); }
    catch (e) { toast.error(`Font Library: ${e.response?.data?.detail || e.message}`); }
    finally { setRemoving(null); }
  };

  const uploadedFonts = fonts.filter(font => font.source !== 'system');

  return <div className={compact ? 'space-y-3' : 'card space-y-4'} data-testid="design-dna-v2-font-library">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--accent-purple)] font-semibold">Font</p>
        <h3 className="text-sm font-semibold mt-1">Font del progetto</h3>
        <p className="text-xs text-[var(--text-muted)] mt-1 max-w-2xl">{compact ? 'Carica famiglie e varianti usate dal Designer V3.' : 'Carica TTF, OTF, WOFF/WOFF2 oppure uno ZIP con tutta la famiglia. Regular, Bold, Italic, Light ecc. restano asset distinti e diventano disponibili direttamente nel Designer V3.'}</p>
      </div>
      <label className={`btn-gradient text-xs cursor-pointer ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
        <UploadSimple size={14}/>{uploading ? 'Caricamento…' : 'Aggiungi font'}
        <input type="file" multiple accept={ACCEPT} className="hidden" onChange={handleFileChange}/>
      </label>
    </div>

    {uploadedFonts.length > 0 ? <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'} gap-2`}>
      {uploadedFonts.map(font => <div key={font.asset_id} className={`rounded-xl border border-[var(--border-color)] ${compact ? 'p-2' : 'p-3'} flex items-center gap-3 min-w-0`}>
        <div className="w-9 h-9 rounded-lg bg-black/30 flex items-center justify-center shrink-0"><TextAa size={16}/></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm truncate">{font.family || font.original_name}</p>
          <p className="text-[10px] text-[var(--text-muted)] truncate">{font.original_name} · {font.weight || 400} · {font.style || 'normal'}</p>
        </div>
        <button type="button" className="text-[var(--accent-pink)]" disabled={removing === font.asset_id} onClick={() => remove(font.asset_id)}><Trash size={14}/></button>
      </div>)}
    </div> : <div className="rounded-xl border border-dashed border-[var(--border-color)] p-4 text-xs text-[var(--text-muted)]">Nessun font personale caricato. Puoi aggiungerli qui oppure usare i font di sistema disponibili nel Designer V3.</div>}
  </div>;
}
